import React, { useEffect, useRef, useState } from "react";
import { XCircle } from "lucide-react";
import LostReasonPicker from "./LostReasonPicker";

/**
 * A "Lost" button that opens a dropdown of lost reasons and calls
 * onSelect(reasonValue, note) when one is picked; note is the agent's own
 * words, asked for when the reason is Other (LostReasonPicker). A reason is always required
 * (matches the holding area + backend). Self-contains its open state and a
 * click-outside handler.
 *
 * Props:
 *  - onSelect(reason, note): required, called with the chosen reason value
 *  - disabled: bool
 *  - align: 'right' | 'left' (dropdown alignment, default 'right')
 *  - className: extra classes for the trigger button
 *  - label: trigger text (default "Lost")
 */
export default function MarkLostMenu({
  onSelect,
  disabled = false,
  align = "right",
  className = "",
  label = "Lost",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={
          className ||
          "px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 text-xs font-bold flex items-center gap-1 hover:bg-gray-50 hover:text-[#c8392f] disabled:opacity-60"
        }
      >
        <XCircle className="w-3 h-3" /> {label}
      </button>
      {open && (
        <div
          className={`absolute ${align === "right" ? "right-0" : "left-0"} z-30 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl py-1`}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400">
            Why was it lost?
          </div>
          <LostReasonPicker
            compact
            onPick={(reason, note) => {
              setOpen(false);
              onSelect(reason, note);
            }}
          />
        </div>
      )}
    </div>
  );
}
